import React, { useEffect, useMemo, useState } from 'react';
import './index.scss';
import { Tooltip } from '@mui/material';
import { navigateTo } from '@/Utils/routing';
import LoadingCircle from '@/Components/LoadingCircle';
import { useUsers } from '@/Recoil/User';
import { useSession } from '@/Recoil/Session/Session';
import { useCharacter } from '@/Recoil/World';
import { logBehavior } from '@/Data/Behavior';
import { useKoreanStudyType } from '@/Recoil/PersonaTest';
import { ErrorSnackbar } from '@/Components/Product';
import { useKoreanStudyTypeContext } from '../KoreanStudyTypeContext';

const adverbs = [
    'Sangat',    // Very
    'Selalu',    // Always
    'Sungguh',   // Truly
    'Amat',      // Extremely
    'Begitu',    // So (much)
    'Makin',     // Increasingly
    'Terus',     // Continuously
    'Penuh',     // Fully
];

const adjectives = [
    'Ceria',     // Cheerful
    'Berani',    // Brave
    'Cerdas',    // Smart
    'Lincah',    // Agile
    'Hebat',     // Great
    'Kuat',      // Strong
    'Ramah',     // Friendly
    'Setia',     // Loyal
    'Cantik',    // Beautiful
    'Gagah',     // Gallant
];

const animals = [
    'Gajah',     // Elephant
    'Harimau',   // Tiger
    'Burung',    // Bird
    'Kucing',    // Cat
    'Kelinci',   // Rabbit
    'Lumba',     // Dolphin
    'Panda',     // Panda
    'Rusa',      // Deer
    'Kuda',      // Horse
    'Elang',     // Eagle
];

const getTempName = (): string => {
    const adverb    = adverbs[Math.floor(Math.random() * adverbs.length)];
    const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const animal    = animals[Math.floor(Math.random() * animals.length)];
    return `${adverb} ${adjective} ${animal}`;
};

const isValidEmail = (email: string): boolean => {
    if (!email) { return true; } // optional field
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

const KoreanStudyTypeSurvey: React.FC = () => {
    const { session } = useSession();
    const { profiles, syncProfiles, updateProfile } = useUsers();
    const { fetchCharacter } = useCharacter();
    const { submitResult } = useKoreanStudyType();
    const { characterSourceImage, startCharacterGeneration } = useKoreanStudyTypeContext();
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    useEffect(() => {
        syncProfiles([session.addressid]);
        fetchCharacter(session.addressid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session.addressid]);

    const profileImage = profiles[session.addressid]?.image;

    const defaultFormData = useMemo(() => ({
        name: profiles[session.addressid]?.name ?? getTempName(),
        email: session.email ?? '',
        whatsapp: profiles[session.addressid]?.phonenumber ?? '+62'
    }), [profiles, session]);

    const [formData, setFormData] = useState(defaultFormData);
    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            name: profiles[session.addressid]?.name || prev.name,
            email: session.email || prev.email,
            whatsapp: profiles[session.addressid]?.phonenumber || prev.whatsapp
        }));
        
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profiles, session]);

    const [isLoading, setIsLoading] = useState(false);

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    return (
        <div className="korean-study-type-survey-container">
            {/* Top Section */}
            <div className="top-section">
                <div className="logo-wrapper">
                    <div className="logo-icon">
                        <img src="/img/matelogoguide.png" alt="MATE Logo" />
                    </div>
                    <span className="logo-text">MATE</span>
                </div>
            </div>

            {/* Content Wrapper */}
            <div className="content-wrapper">
                {/* Contents Section */}
                <div className="contents-section">
                    {/* Question Section */}
                    <div className="question-section">
                        <h2 className="section-title">{"Masukkan Informasimu"}</h2>

                        <div className='mandatory-inputs'>
                            {/* Email Field */}
                            <div className="input-group">
                                <label className="input-label">{"Email"}</label>
                                <div className="input-box">
                                    <input
                                        type="email"
                                        placeholder="Masukkan emailmu: example@example.com"
                                        value={formData.email}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        onBlur={(e) => {
                                            if (e.target.value !== defaultFormData.email) {
                                                logBehavior('personatest:251212kst:input:email', { email: e.target.value });
                                            }
                                        }}
                                    />
                                </div>
                                <div className="login-method">
                                    <button onClick={() => {
                                        logBehavior('personatest:251212kst:button_press:login_google');
                                        navigateTo('/login?redirectTo=/personatest/koreanstudytype/survey')
                                    }}>
                                        <svg width="20" height="20" viewBox="0 0 24 24">
                                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                        </svg>
                                    </button>
                                    <button onClick={() => {
                                        logBehavior('personatest:251212kst:button_press:login_mateapp');
                                        navigateTo('/login?redirectTo=/personatest/koreanstudytype/survey')
                                    }}>
                                        <img src={"/logo-pink.svg"} alt="Korean Mate Logo" style={{width: '20px', height: '20px'}}/>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="input-divider" />

                        {/* Name Field */}
                        <div className="input-group">
                            <label className="input-label">{"Nama"}<span style={{color:'#BBBBBB'}}>{" optional"}</span></label>
                            <div className="input-box">
                                <input
                                    type="text"
                                    placeholder="Masukkan namamu"
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    onBlur={(e) => {
                                        if (e.target.value !== defaultFormData.name) {
                                            logBehavior('personatest:251212kst:input:name', { name: e.target.value });
                                        }
                                    }}
                                />
                            </div>
                        </div>
                        {/* WhatsApp Field */}
                        <div className="input-group">
                            <label className="input-label">{"Nomor Whatsapp"}<span style={{color:'#BBBBBB'}}>{" optional"}</span></label>
                            <div className="input-box">
                                <input
                                    type="tel"
                                    placeholder="Masukkan nomor Whatsappmu"
                                    value={formData.whatsapp}
                                    onChange={(e) => handleInputChange('whatsapp', e.target.value)}
                                    onBlur={(e) => {
                                        if (e.target.value !== defaultFormData.whatsapp) {
                                            logBehavior('personatest:251212kst:input:whatsapp', { whatsapp: e.target.value });
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="cta-section">
                <div className="cta-wrapper">
                    <Tooltip
                        title={!!formData.email && !isValidEmail(formData.email) ? "Masukkan email yang valid." : ""}
                        placement="top"
                    >
                        <button
                            className="btn-submit"
                            disabled={!formData.email || !isValidEmail(formData.email)}
                            onClick={() => {
                                setIsLoading(true);
                                (async()=>{
                                    let successfullyUpdated = await updateProfile({
                                        name: formData.name,
                                        email: formData.email,
                                        phonenumber: formData.whatsapp,
                                    }).catch(() => {
                                        logBehavior('personatest:251212kst:updateProfileFailed', { formData });
                                    });

                                    if (!successfullyUpdated) {
                                        setIsLoading(false);
                                        setShowSnackbar(true);
                                        setSnackbarMessage('Email ini sudah terdaftar. Bagi pengguna MATE, harap login dulu sebelum melanjutkan.\nBagi yang bukan pengguna MATE, gunakan email milik Anda sendiri.');
                                        setTimeout(() => {
                                            setShowSnackbar(false);
                                            setSnackbarMessage('');
                                        }, 1500);
                                        return;
                                    }

                                    // Start character generation in background if profile image is provided and different from existing
                                    if (profileImage && profileImage !== characterSourceImage) {
                                        startCharacterGeneration(profileImage);
                                    }

                                    const result = await submitResult(session.addressid);
                                    if (!result) {
                                        logBehavior('personatest:251212kst:submitResultFailed', {});
                                    }

                                    setIsLoading(false);
                                    navigateTo(`/personatest/koreanstudytype/preview/${session.addressid}/${result?.type ?? 'casual_learner'}`);
                                })();
                            }}
                        >{"Continue"}</button>
                    </Tooltip>
                </div>
            </div>
            <ErrorSnackbar show={showSnackbar} message={snackbarMessage}/>
            <LoadingCircle show={isLoading} />
        </div>
    );
};

export default KoreanStudyTypeSurvey;
